import type {
  SupplierDocument,
  SupplierDocumentType,
  SupplierType,
} from '../api';

const CANDIDATE_STATUSES = new Set(['enviado', 'reprovado']);

function documentRank(left: SupplierDocument, right: SupplierDocument): number {
  if (left.version !== right.version) return left.version - right.version;
  const leftCreatedAt = new Date(left.createdAt).getTime();
  const rightCreatedAt = new Date(right.createdAt).getTime();
  if (leftCreatedAt !== rightCreatedAt) return leftCreatedAt - rightCreatedAt;
  return left.id.localeCompare(right.id);
}

function newest(documents: SupplierDocument[]): SupplierDocument | undefined {
  return documents.slice().sort((left, right) => documentRank(right, left))[0];
}

export interface SupplierDocumentVersions {
  current?: SupplierDocument;
  candidate?: SupplierDocument;
  history: SupplierDocument[];
}

export function selectSupplierDocumentVersions(
  documents: SupplierDocument[],
  documentType: string,
): SupplierDocumentVersions {
  const versions = documents.filter((document) => document.documentType === documentType);
  const hasExplicitCurrentFlag = versions.some((document) => typeof document.isCurrent === 'boolean');
  const current = hasExplicitCurrentFlag
    ? newest(versions.filter((document) => document.isCurrent === true))
    : newest(versions);
  const candidate = newest(versions.filter((document) => (
    document.id !== current?.id
    && document.isCurrent !== true
    && CANDIDATE_STATUSES.has(document.status)
    && (!current || documentRank(document, current) > 0)
  )));
  const history = versions
    .filter((document) => document.id !== current?.id && document.id !== candidate?.id)
    .sort((left, right) => documentRank(right, left));

  return { current, candidate, history };
}

export function documentTypeAppliesToSupplier(
  documentType: SupplierDocumentType,
  supplierType: SupplierType,
): boolean {
  if (!documentType.requiredForCorporate && !documentType.requiredForIndividual) return true;
  return supplierType === 'pessoa_juridica'
    ? documentType.requiredForCorporate
    : documentType.requiredForIndividual;
}

export function supplierDocumentCodes(
  supplierType: SupplierType,
  requiredDocuments: string[],
  documents: SupplierDocument[],
  documentTypes: SupplierDocumentType[],
): string[] {
  const codes: string[] = [];
  const append = (code: string) => {
    if (code && !codes.includes(code)) codes.push(code);
  };

  requiredDocuments.forEach(append);
  documentTypes
    .filter((documentType) => documentType.active && documentTypeAppliesToSupplier(documentType, supplierType))
    .forEach((documentType) => append(documentType.code));
  [...new Set(documents.map((document) => document.documentType))]
    .sort((left, right) => left.localeCompare(right, 'pt-BR'))
    .forEach(append);

  return codes;
}

export function supplierUploadDocumentTypes(
  supplierType: SupplierType,
  requiredDocuments: string[],
  documents: SupplierDocument[],
  documentTypes: SupplierDocumentType[],
): SupplierDocumentType[] {
  const visibleCodes = new Set(supplierDocumentCodes(
    supplierType,
    requiredDocuments,
    documents,
    documentTypes,
  ));
  return documentTypes.filter((documentType) => documentType.active && visibleCodes.has(documentType.code));
}
